'use client';

import {
    ComposableContent,
    GlowAdvancedCard,
    GlowClickable,
    GlowIcon,
    GlowTag,
    GlowTextBody,
    GlowTextBodySmall,
    IconName,
    Icons,
} from '@headless-workspace/glow-ds';
import { Component, DeliveryMethod } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import { UIStoreOpenHours } from './UIStoreOpenHours';

export type UIStoreInfoCardProps = {
    favoriteLabel: string;
    store: {
        id: string;
        name: string;
        addressText: string;
        distanceText: string;
        productAvailability: {
            isAvailable: boolean;
            text: string;
        };
        scheduleInfo: {
            openStatusTitle: string;
            openStatusText: string;
            openHours: { day: string; hours: string }[];
        };
        shippingMethodInfo: {
            groupId: DeliveryMethod;
            freeLabel?: string;
            isProductDTSCompliant?: boolean;
            pickupLabel?: string;
            deliveryInfosModal?: boolean;
        };
        isFavoriteStore: boolean;
        isSelected: boolean;
    };
    closeOptions?: {
        label: string;
        onClose: () => void;
    };
    onSelect?: (id: string) => void;
    handleDTSInfoClick: () => void;
};

const HYPHEN_PREFIX = '\u00A0- ';

export const UIStoreInfoCard: Component<UIStoreInfoCardProps> = ({
    store,
    closeOptions,
    onSelect,
    favoriteLabel,
    handleDTSInfoClick,
}) => {
    const handleStoreSelection = () => {
        onSelect?.(store.id);
    };

    const { freeLabel, pickupLabel, deliveryInfosModal } = store.shippingMethodInfo;

    return (
        <GlowAdvancedCard
            SelectableTag={'button'}
            isSelected={store.isSelected}
            onSelectCard={handleStoreSelection}
            className={'overflow-visible'}
        >
            {store.isFavoriteStore && (
                <GlowTag label={favoriteLabel} size={'medium'} color={'primaryBlue'} iconName={IconName.Star} />
            )}
            <div className={'flex items-center'}>
                {onSelect ? (
                    <GlowClickable
                        onClick={handleStoreSelection}
                        content={{
                            labelElement: <GlowTextBody text={store.name} fontWeight={'bold'} />,
                        }}
                    />
                ) : (
                    <GlowTextBody text={store.name} fontWeight={'bold'} />
                )}
                <GlowTextBodySmall
                    text={StringHelpers.addPrefix(store.distanceText, HYPHEN_PREFIX, true)}
                    color={'tertiary'}
                />
            </div>
            <GlowTextBodySmall
                text={store.productAvailability.text}
                fontWeight={'bold'}
                color={store.productAvailability.isAvailable ? 'success' : 'error'}
            />
            <GlowTextBodySmall text={store.addressText} color={'tertiary'} />

            <ComposableContent.Accordion
                accordion={{
                    title: (
                        <span className={'flex'}>
                            <GlowTextBodySmall
                                TagName={'span'}
                                text={store.scheduleInfo.openStatusTitle}
                                fontWeight={'bold'}
                            />
                            <GlowTextBodySmall
                                text={StringHelpers.addPrefix(store.scheduleInfo.openStatusText, HYPHEN_PREFIX, true)}
                                fontWeight={'medium'}
                                TagName={'span'}
                            />
                        </span>
                    ),
                    content: <UIStoreOpenHours openHours={store.scheduleInfo.openHours} textSize={'small'} />,
                }}
            />

            <div className={'flex items-center justify-between'}>
                {pickupLabel ? (
                    <div className={'flex items-center gap-0.25'}>
                        <GlowTextBodySmall text={pickupLabel} fontWeight={'bold'} />
                        {deliveryInfosModal && (
                            <GlowClickable
                                className={'z-2'}
                                onClick={handleDTSInfoClick}
                                content={{
                                    icon: <GlowIcon Icon={Icons.Info} type={'large'} />,
                                }}
                            />
                        )}
                    </div>
                ) : null}
                {freeLabel && <GlowTag label={freeLabel} size={'large'} color={'primaryGreen'} />}
            </div>
            {closeOptions && (
                <GlowClickable
                    className={'absolute right-1 top-1'}
                    ariaLabel={closeOptions.label}
                    content={{
                        icon: <GlowIcon Icon={Icons.Cross} type={'large'} />,
                    }}
                    onClick={closeOptions.onClose}
                />
            )}
        </GlowAdvancedCard>
    );
};
